'use client';

import { useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { getLinkPathname, parseHashLink } from '@/app/lib/utils';

export function SmoothScrollHandler({ offset = 200 }) {
  const pathname = usePathname();

  const smoothScrollTo = useCallback((el, hash) => {
    const targetTop = el.getBoundingClientRect().top + window.scrollY - offset;
    const start = window.scrollY;
    const distance = targetTop - start;
    const duration = 500; // ms
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      window.scrollTo(0, start + distance * ease);

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);

    // update URL hash
    if (hash) {
      // Use replaceState to avoid cluttering history with scroll updates
      history.replaceState(null, '', `#${hash}`);
    }
  }, [offset]);

  useEffect(() => {
    const handleClick = (e) => {
      // Find the closest anchor tag
      const link = e.target.closest('a');
      if (!link) return;

      // Ignore if it's a submenu trigger (handled elsewhere)
      if (link.dataset.isSubmenuTrigger === 'true') {
        return;
      }

      const href = link.getAttribute('href');
      const parsed = parseHashLink(href);
      if (!parsed) return;

      const { rawPath, hash } = parsed;
      const currentPath = pathname.replace(/\/$/, '');
      const linkPath = getLinkPathname(rawPath);
      const samePage = linkPath === '' || linkPath === currentPath;

      if (samePage) {
        e.preventDefault();
        const el = document.getElementById(hash);
        if (el) {
          smoothScrollTo(el, hash);
        }
      }
    };

    document.addEventListener('click', handleClick);

    // Auto scroll on initial load (must be wrapped in a timeout to ensure DOM is ready)
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      const el = document.getElementById(hash);
      
      // Increased timeout to 500ms for greater reliability in complex Next.js renders
      if (el) {
        setTimeout(() => smoothScrollTo(el, hash), 500); 
      }
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [pathname, smoothScrollTo]);

  return null;
}
